import classNames from 'classnames';
import { observer } from 'mobx-react';
import { CSSProperties, useEffect, useRef, useState } from 'react';

import ExternalImage from '../../../shared/components/ExternalImage/ExternalImage';
import ModalCarousel from '../ModalCarousel/ModalCarousel';
import css from './Gallery.css';
import { GalleryState, ImageModel } from './GalleryState';

// Gap between images, in pixels — used both in the layout math and as the flex `gap`.
const GAP = 8;

type LaidOutImage = {
  image: ImageModel;
  width: number;
  height: number;
};

const getAspectRatio = (image: ImageModel) => (image.size ? image.size.width / image.size.height : 1);

// Largest breakpoint whose minWidth still fits the container wins (breakpoints are largest-first).
const getItemsPerRow = (breakpoints: GalleryBreakpoint[], containerWidth: number, fallback: number) =>
  breakpoints.filter((b) => b.minWidth < containerWidth).first()?.itemsPerRow ?? fallback;

// Lay the visible images out into justified rows: every image in a row shares the same height, and
// widths are derived from each image's aspect ratio so the row exactly fills the container width.
// Returns a flat list in source order, so each item's array index matches its index in `images`.
const computeLayout = (
  images: ImageModel[],
  breakpoints: GalleryBreakpoint[],
  maxRowCount: number,
  containerWidth: number,
): LaidOutImage[] => {
  if (containerWidth <= 0 || images.length === 0) {
    return [];
  }

  const itemsPerRow = getItemsPerRow(breakpoints, containerWidth, images.length);
  const rowCount = Math.min(maxRowCount, Math.floor(images.length / itemsPerRow));
  // With fewer images than a single row, render them all in one row; otherwise cap to whole rows.
  const perRow = rowCount === 0 ? images.length : itemsPerRow;
  const renderCount = rowCount === 0 ? images.length : rowCount * itemsPerRow;

  const layout: LaidOutImage[] = [];

  for (let start = 0; start < renderCount; start += perRow) {
    const slice = images.slice(start, start + perRow);
    const ratios = slice.map(getAspectRatio);
    const sumAspect = ratios.reduce((sum, ratio) => sum + ratio, 0);
    const availableWidth = containerWidth - (slice.length - 1) * GAP;
    const rowHeight = availableWidth / sumAspect;

    layout.push(
      ...slice.map((image, i) => ({
        image,
        width: Math.floor(rowHeight * ratios[i]),
        height: rowHeight,
      })),
    );
  }

  return layout;
};

const useElementWidth = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  return [ref, width] as const;
};

export type GalleryBreakpoint = {
  minWidth: number;
  itemsPerRow: number;
};

export type GalleryProps = {
  breakpoints: GalleryBreakpoint[];
  maxRowCount: number;
  state: GalleryState;
  className?: string;
  style?: CSSProperties;
  withBackground?: boolean;
};

export const Gallery = observer(
  ({ breakpoints, maxRowCount, state, className, style, withBackground }: GalleryProps) => {
    const [wrapperRef, containerWidth] = useElementWidth<HTMLDivElement>();

    const hasBackground = withBackground || !!style?.background || !!style?.backgroundColor;
    const layout = computeLayout(state.images, breakpoints, maxRowCount, containerWidth);

    return (
      <>
        <div className={className} style={style}>
          <div className={classNames(css.inner, { [css.withBackground]: hasBackground })}>
            <div ref={wrapperRef} className={css.galleryWrapper} style={{ gap: `${GAP}px` }}>
              {layout.map(({ image, width, height }, index) => (
                <ExternalImage
                  alt={image.name}
                  src={image.optimizedUrl}
                  width={width}
                  height={height}
                  style={{ cursor: 'pointer' }}
                  onClick={() => state.openLightbox(index)}
                  key={image.optimizedUrl}
                />
              ))}
            </div>
          </div>
        </div>

        <ModalCarousel
          initialSlide={state.initialImageIndex}
          images={state.images}
          isOpen={state.isLightboxOpen}
          onClose={state.closeLightbox}
          onDownload={state.downloadImage}
        />
      </>
    );
  },
);
